/**
 * Géométrie pure du tableau des annonces : jours affichés, mois, durée de
 * boucle. Tout en clés `AAAA-MM-JJ` traitées en UTC — jamais de date locale,
 * sinon une colonne glisse d'un jour selon le fuseau du poste.
 */

export const INTRO_SEC_DEFAULT = 8
export const DISPLAY_SEC_DEFAULT = 10

const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
const WEEKDAYS_SHORT = ['D', 'L', 'M', 'M', 'J', 'V', 'S']

function parse(day: string): Date {
  const [year, month, date] = day.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, date))
}

function key(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
}

export function addDays(day: string, delta: number): string {
  const date = parse(day)
  date.setUTCDate(date.getUTCDate() + delta)
  return key(date)
}

/** Jours d'une période inclusive ; vide si elle finit avant de commencer. */
export function dayRangeKeys(from: string, to: string, maxDays = 800): string[] {
  const days: string[] = []
  for (let day = from; day <= to && days.length < maxDays; day = addDays(day, 1)) days.push(day)
  return days
}

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Vrai pour un jour civil `AAAA-MM-JJ` qui existe. Un champ date en cours de
 * saisie peut valoir `20102-10-06` (année à cinq chiffres) : on ne l'envoie pas,
 * le serveur le refuserait. Miroir de `isDayKey` côté backend.
 */
export function isDayKey(value: string): boolean {
  const match = DAY_KEY.exec(value)
  if (!match) return false
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

/** Période envoyable : deux jours valides, dans l'ordre. */
export function isDayRange(from: string, to: string): boolean {
  return isDayKey(from) && isDayKey(to) && from <= to
}

export interface DayColumn {
  day: string
  dayOfMonth: number
  weekdayLetter: string
  weekend: boolean
  today: boolean
  /** Premier jour d'un mois dans la fenêtre : porte un filet de séparation. */
  monthStart: boolean
}

export interface MonthSpan { label: string; span: number }

export function dayColumns(from: string, count: number, today: string): DayColumn[] {
  return Array.from({ length: count }, (_, index) => {
    const day = addDays(from, index)
    const date = parse(day)
    const weekday = date.getUTCDay()
    return {
      day,
      dayOfMonth: date.getUTCDate(),
      weekdayLetter: WEEKDAYS_SHORT[weekday],
      weekend: weekday === 0 || weekday === 6,
      today: day === today,
      monthStart: index === 0 || date.getUTCDate() === 1,
    }
  })
}

export function monthSpans(columns: readonly DayColumn[]): MonthSpan[] {
  const spans: MonthSpan[] = []
  for (const column of columns) {
    const date = parse(column.day)
    const label = `${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`
    const last = spans[spans.length - 1]
    if (last && last.label === label) last.span++
    else spans.push({ label, span: 1 })
  }
  return spans
}

/** « 1 min 38 s » ; « 45 s » ; « 2 min ». */
export function formatLoop(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  if (minutes === 0) return `${rest} s`
  return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`
}

const MONTHS_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']

/** « 9 oct. » pour les étiquettes de période. */
export function shortDayLabel(day: string): string {
  const date = parse(day)
  return `${date.getUTCDate() === 1 ? '1er' : date.getUTCDate()} ${MONTHS_SHORT[date.getUTCMonth()]}`
}

/** « 09/10/26 », comme la colonne « Mail du » de la feuille. */
export function sheetDate(day: string | null): string {
  if (!day) return ''
  const [year, month, date] = day.split('-')
  return `${date}/${month}/${year.slice(2)}`
}

/**
 * Libellé de l'état d'une annonce pour un jour, du plus grave au plus
 * informatif. Une annonce sans image cochée ce jour ne passera pas.
 */
export type AnnouncementDayState = 'off' | 'onAir' | 'missingImage' | 'cancelled' | 'archived'

export function dayState(input: {
  checked: boolean
  imageAvailable: boolean
  cancelled: boolean
  archived: boolean
}): AnnouncementDayState {
  if (!input.checked) return 'off'
  if (input.archived) return 'archived'
  if (input.cancelled) return 'cancelled'
  return input.imageAvailable ? 'onAir' : 'missingImage'
}
